<template>
    <header
        :class="$style.header"
        data-interface-section="desktop-header"
    >
        <Link
            :to="{ name: ROUTE_NAMES.MAIN }"
            data-interface-target="navigation-link"
            :class="$style.icon_wrapper"
            hoverAnimation="rainbow"
        >
            <Icon
                name="playgama_logo"
                :class="$style.icon_logo"
            />
        </Link>
        <TransitionGroup
            tag="div"
            :enterFromClass="$style.fade_enter_from"
            :enterActiveClass="$style.fade_enter_active"
            :moveClass="$style.fade_move"
            :leaveActiveClass="$style.fade_leave_active"
            :leaveToClass="$style.fade_leave_to"
            :class="$style.flexible_block"
        >
            <TasksWidget
                key="tasks"
                :class="$style.widgets_override"
                redirect
            />
            <RewardsWidget key="rewards" />
            <template v-if="isUserInfoLoaded && isMounted">
                <template v-if="user && isAuthorized">
                    <UserWidget
                        key="user"
                        :user="user"
                        :isProfilePage="isUserProfilePage"
                        :userBalance="userBalance"
                    />
                    <LogoutButton
                        v-if="isUserProfilePage"
                        key="logout"
                        @logout="logout"
                    />
                </template>
                <Button
                    v-else
                    key="signin"
                    size="xl"
                    interfaceTarget="open-sign-in-modal"
                    @click="signin"
                >
                    {{ $tr('mobileHeader.login') }}
                </Button>
            </template>
            <div
                key="spacer"
                :class="$style.spacer"
            />
        </TransitionGroup>
        <div :class="$style.right_block">
            <HeaderTiles />
        </div>
    </header>
</template>
<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import Button from '@/components_new/Button.vue'
import Icon from '@/components_new/Icon/Icon.vue'
import Link from '@/components_new/Link.vue'
import TasksWidget from '@/components_new/TaskItems/TasksWidget.vue'
import { ROUTE_NAMES } from '@/router/constants'
import { useLayoutStore } from '@/store/layout-store'
import { useUserStore } from '@/store/user-store/user-store'
import { useIsMounted } from '@/utils/vue-hooks/use-is-mounted'

import HeaderTiles from './components/HeaderTiles.vue'
import LogoutButton from './components/LogoutButton.vue'
import RewardsWidget from './components/RewardsWidget.vue'
import UserWidget from './components/UserWidget.vue'

const layoutStore = useLayoutStore()
const userStore = useUserStore()
const router = useRouter()
const route = useRoute()
const isMounted = useIsMounted()
const isUserProfilePage = computed(() => route.name === ROUTE_NAMES.PROFILE)
const { isAuthorized, isUserInfoLoaded, user, userBalance } = storeToRefs(userStore)

async function logout() {
    await userStore.logout()
    router.replace({ name: ROUTE_NAMES.MAIN })
}

function signin() {
    layoutStore.setSignInModalOpened(true)
}
</script>
<style module>
.header {
    display: flex;
    align-items: center;
    height: 100%;
    gap: 4px;
}

.widgets_override {
    flex-grow: 1;
    max-width: 280px;
}

.spacer {
    flex-grow: 2;
}

.flexible_block {
    display: flex;
    flex-grow: 1;
    align-items: center;
    gap: 4px;
}

.right_block {
    margin-left: auto;
}

.fade_move,
.fade_enter_active,
.fade_leave_active {
    transition: transform 200ms ease-out;
}

.fade_enter_from,
.fade_leave_to {
    opacity: 0;
    transform: translate(5%, 0);
}

/* ensure leaving items are taken out of layout flow so that moving animations can be calculated correctly. */
.fade_leave_active {
    position: absolute;
}

.icon_wrapper {
    border-radius: 26px;
    background-color: var(--purple-400);
    padding: 10px 20px;
    height: 32px;
}

.icon_wrapper:hover,
.icon_wrapper:focus {
    filter: brightness(1.08);
}

.icon_logo {
    height: 32px;
    width: 148px;
    color: var(--white);
}

.icon_wrapper:hover .icon_logo,
.icon_wrapper:focus .icon_logo {
    color: var(--black);
}
</style>
